// src/components/groups/InviteModal.js

import { createNotification } from '../../services/notificationService';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Mail, Send, Plus, X } from 'lucide-react';
import { auth } from '../../config/firebase';
import { db } from '../../config/firebase';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';

function InviteModal({ group, isOpen, onClose }) {
  const [emails, setEmails] = useState(['']);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingInvites, setPendingInvites] = useState([]);

  // Fetch pending invitations when modal opens
  useEffect(() => {
    if (isOpen && group) {
      const fetchPendingInvites = async () => {
        try {
          const invitesQuery = query(
            collection(db, 'invitations'),
            where('groupId', '==', group.id),
            where('status', '==', 'pending')
          );

          const invitesSnapshot = await getDocs(invitesQuery);
          const invitesData = invitesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          setPendingInvites(invitesData);
        } catch (error) {
          console.error('Error fetching pending invites:', error);
        }
      };
      
      fetchPendingInvites();
    }
  }, [isOpen, group]);

  const fetchPendingInvites = async () => {
    try {
      const invitesQuery = query(
        collection(db, 'invitations'),
        where('groupId', '==', group.id),
        where('status', '==', 'pending')
      );

      const invitesSnapshot = await getDocs(invitesQuery);
      const invitesData = invitesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setPendingInvites(invitesData);
      console.log('Fetched pending invites:', invitesData);
    } catch (error) {
      console.error('Error fetching pending invites:', error);
    }
  };

  const addEmailInput = () => {
    setEmails([...emails, '']);
  };

  const removeEmailInput = (indexToRemove) => {
    setEmails(emails.filter((_, index) => index !== indexToRemove));
  };

  const updateEmail = (index, value) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };

  const validateEmails = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const validEmails = emails.filter(email => email.trim() !== '');
    
    if (validEmails.length === 0) {
      setError('Please enter at least one email address');
      return false;
    }

    for (const email of validEmails) {
      if (!emailRegex.test(email)) {
        setError(`Invalid email format: ${email}`);
        return false;
      }
    }
    return true;
  };

  // Add this function to send invitation emails
  const sendInvitationEmail = async (email, groupName, inviterName, message) => {
    try {
      const response = await fetch('https://us-central1-semaphore-app-db378.cloudfunctions.net/sendEmailHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          to: email,
          subject: `${inviterName} invited you to join ${groupName} on Semafor App`,
          text: `${inviterName} has invited you to join ${groupName} on Semafor App.`,
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
              <h2>You've been invited to join a group</h2>
              <p><strong>${inviterName}</strong> has invited you to join <strong>${groupName}</strong> on Semafor App.</p>
              ${message ? `<p>Message: "${message}"</p>` : ''}
              <p>Sign in to your account to accept the invitation.</p>
              <p>If you don't have an account yet, you can create one to join the group.</p>
            </div>
          `
        }),
      });
      
      const data = await response.json();
      console.log('Email sent successfully:', data);
      return data;
    } catch (error) {
      console.error('Error sending email:', error);
      // Don't throw the error, just log it
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Check if current user is the creator
    if (group.createdBy !== auth.currentUser?.uid) {
      setError('Only the group creator can send invitations');
      setIsLoading(false);
      return;
    }

    if (!validateEmails()) {
      setIsLoading(false);
      return;
    }

    try {
      const validEmails = emails.filter(email => email.trim() !== '');
      const currentUser = auth.currentUser;
      
      console.log('Sending invites to:', validEmails);
      
      // Create invitations in Firestore
      const batch = [];
      
      for (const email of validEmails) {
        // Check if there's already a pending invitation
        const existingInvites = await getDocs(
          query(
            collection(db, 'invitations'),
            where('email', '==', email),
            where('groupId', '==', group.id),
            where('status', '==', 'pending')
          )
        );

        if (existingInvites.empty) {
          // Create new invitation
          const invitation = {
            groupId: group.id,
            groupName: group.name,
            email: email,
            status: 'pending',
            invitedBy: currentUser.uid,
            invitedByName: currentUser.displayName || 'A user',
            message: message,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            // Set expiration to 7 days from now
            expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
          };

          console.log('Creating invitation:', invitation);
          const docRef = await addDoc(collection(db, 'invitations'), invitation);
          console.log('Created invitation with ID:', docRef.id);
          
          // Send email notification
          await sendInvitationEmail(
            email,
            group.name,
            currentUser.displayName || 'A user',
            message
          );
          
          batch.push(docRef);
        } else {
          console.log('Invitation already exists for:', email);
        }
      }

      // Refresh pending invites
      await fetchPendingInvites();
      
      // Reset form
      setEmails(['']);
      setMessage('');
      
      console.log('Successfully sent invitations');

      if (batch.length > 0) {
        const pluralText = batch.length === 1 ? '' : 's';
        await createNotification(
          currentUser.uid, 
          'invite',
          'sent',
          `You sent ${batch.length} invitation${pluralText} to join ${group.name}`,
          {
            relatedGroupId: group.id,
            relatedGroupName: group.name
          }
        );
      }

    } catch (error) {
      console.error('Failed to send invites:', error);
      setError('Failed to send invites: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Invite Members</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 rounded-md">
              {error}
            </div>
          )}

          {/* Email Input Section */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">
                Email Addresses
              </label>
              <div className="mt-2 space-y-2">
                {emails.map((email, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Input 
                      value={email}
                      onChange={(e) => updateEmail(index, e.target.value)}
                      type="email"
                      placeholder="Enter email address"
                      className="w-full p-2 border rounded-md"
                    />
                    {emails.length > 1 && (
                      <Button 
                        type="button"
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeEmailInput(index)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}

                <Button 
                  type="button"
                  variant="ghost" 
                  size="sm"
                  onClick={addEmailInput}
                  className="text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add another email</span>
                </Button>
              </div>
            </div>

            {/* Pending Invites Section */}
            {pendingInvites.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Pending Invites
                </label>
                <div className="p-3 bg-gray-50 rounded-md divide-y divide-gray-100">
                  {pendingInvites.map((invite) => (
                    <div key={invite.id} className="flex items-center justify-between py-2">
                      <div className="flex items-center space-x-2 text-sm">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span>{invite.email}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {invite.createdAt && new Date(invite.createdAt.seconds * 1000).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Invitation Message */}
            <div>
              <label className="text-sm font-medium text-gray-700">
                Add a Message (Optional)
              </label>
              <textarea 
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full mt-1 p-2 border rounded-md h-24 resize-none"
                placeholder="Write a personal message to include with the invitation..."
              />
            </div>
          </div>

          <div className="flex space-x-2 justify-end pt-4 border-t">
            <Button 
              type="button"
              variant="outline" 
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={isLoading}
              className="flex items-center space-x-2"
            >
              <Send className="h-4 w-4" />
              <span>{isLoading ? 'Sending...' : 'Send Invites'}</span>
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default InviteModal;
